import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Card, Button, Nav, Tab, Spinner } from 'react-bootstrap';
import { useGetCustomerById } from '../../../services/customerService';
import type { Customer } from '../../../services/schemas/customerServiceSchemas/customer';
import type { CustomerStatus } from '../../../services/schemas/customerServiceSchemas/customerStatus';
import type { Corporate } from '../../../services/schemas/customerServiceSchemas/corporate';
import type { Address } from '../../../services/schemas/customerServiceSchemas/address';
import type { ContactDetail } from '../../../services/schemas/customerServiceSchemas/contactDetail';
import type { BankDetail } from '../../../services/schemas/customerServiceSchemas/bankDetail';
import { formatDate } from '../../../utils/formatters';
import CustomerComments from '../../../components/common/CustomerComments';
import CustomerCollectionNotes from '../../../components/common/CustomerCollectionNotes';
import CustomerChargeTermsCard from '../../../components/common/CustomerChargeTermsCard';
import './CustomerDetails.css';
import { LEGAL_ENTITY_TYPES } from '../../../utils/validation/legalEntityType';

const statusVariant = (status?: CustomerStatus) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'danger';
    case 'SUSPENDED':
      return 'warning';
    default:
      return 'secondary';
  }
};

const LabelValue: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="lv d-flex align-items-start py-2">
    <div className="lv-label text-muted small me-3">{label}</div>
    <div className="lv-value text-dark fw-semibold flex-grow-1">{value ?? <span className="text-muted">—</span>}</div>
  </div>
);

const SectionCard: React.FC<{ title: string; icon?: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <Card className="border-0 shadow-sm mb-3">
    <Card.Header className="bg-white border-0 py-3 d-flex align-items-center">
      {icon && (
        <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
          <i className={`fa ${icon} text-primary`} />
        </div>
      )}
      <h6 className="mb-0 fw-bold">{title}</h6>
    </Card.Header>
    <Card.Body className="pt-0 px-3 pb-3">
      {children}
    </Card.Body>
  </Card>
);

// Business-facing wording for the stored codes, so a screen never shows SOLE_PROPRIETOR.
// One list of legal forms for the whole UI; see utils/validation/legalEntityType.
const ENTITY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  LEGAL_ENTITY_TYPES.map(type => [type.code, type.label])
);

const REGISTRATION_NUMBER_TYPE_LABELS: Record<string, string> = {
  CIPC: 'CIPC',
  PRACTICE_NUMBER: 'Practice number',
  TRUST_NUMBER: 'Trust number',
  ID_NUMBER: 'ID number',
  OTHER: 'Other',
};

const REPRESENTATIVE_CAPACITY_LABELS: Record<string, string> = {
  DIRECTOR: 'Director',
  MEMBER: 'Member',
  TRUSTEE: 'Trustee',
  PARTNER: 'Partner',
  EMPLOYEE: 'Employee',
  ATTORNEY: 'Attorney',
  AGENT: 'Agent',
  OTHER: 'Other',
};

const CustomerDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetCustomerById(id || '', { query: { enabled: !!id } }) as unknown as {
    data?: Customer;
    isLoading: boolean;
    error?: { message?: string } | null;
  };

  const corp = useMemo(() => (data?.info && (data.info as any).type === 'CORPORATE' ? (data.info as Corporate) : undefined), [data]);

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/">
                    <i className="fa fa-home me-2" />Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/loan-customers">Business Customers</Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">View Customer</li>
              </ol>
            </div>
            <div className="col-auto">
              <div className="d-flex gap-2">
                <Button size="sm" variant="light" onClick={() => navigate('/loan-customers')}>
                  <i className="fa fa-arrow-left me-2" /> Back
                </Button>
                <Button size="sm" variant="light" onClick={() => navigate(`/loan-customers/${data?.id}/edit`)}>
                  <i className="fa fa-edit me-2" /> Edit
                </Button>
                <Button size="sm" variant="light" onClick={() => navigate(`/loan-customers/${data?.id}/documents`)}>
                  <i className="fa fa-file me-2" /> Documents
                </Button>
                {data?.customerNumber && (
                  <Button size="sm" variant="light" onClick={() => navigate(`/customers/${data.customerNumber}/accounts`)}>
                    <i className="fa fa-credit-card me-2" /> Accounts
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="fs-3 fw-bold mb-1 text-white">{corp?.registeredName || data?.customerNumber || 'Customer'}</h1>
              <div className="d-flex align-items-center gap-3 text-white-50">
                <div>Customer No: <span className="text-white fw-semibold">{data?.customerNumber || '—'}</span></div>
                <div>
                  Status: <span className="text-white fw-semibold">{data?.status || '—'}</span>
                </div>
                {data?.preferredCommChannel && (
                  <div>Pref. Channel: <span className="text-white fw-semibold">{data.preferredCommChannel}</span></div>
                )}
              </div>
            </div>
            <div className="col-md-4 text-md-end text-white-50">
              <div className="small">Created: <span className="text-white">{data?.createdAt ? formatDate(data.createdAt) : '—'}</span></div>
              <div className="small">Updated: <span className="text-white">{data?.updatedAt ? formatDate(data.updatedAt) : '—'}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading customer...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error?.message || 'Failed to load customer'}</div>
          ) : !data ? (
            <div className="alert alert-warning">Customer not found</div>
          ) : (
            <Tab.Container defaultActiveKey="overview">
            <div className="card border-0 shadow-sm mb-3 customer-details-tab-card">
              <div className="card-body customer-details-tab-body">
                <Nav variant="pills" className="customer-details-tabs flex-wrap gap-2">
                    <Nav.Item>
                      <Nav.Link eventKey="overview"><i className="fa fa-dashboard me-2" />Overview</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="company"><i className="fa fa-building me-2" />Company Info</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="addresses"><i className="fa fa-map-marker me-2" />Addresses</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="contacts"><i className="fa fa-users me-2" />Contacts</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="bank"><i className="fa fa-university me-2" />Bank Details</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="directors"><i className="fa fa-id-badge me-2" />Directors</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="comments"><i className="fa fa-comments me-2" />Comments</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
              </div>

              <Tab.Content>
                <Tab.Pane eventKey="overview">
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <SectionCard title="Identity" icon="fa-address-card">
                        <LabelValue label="Customer Number" value={data.customerNumber} />
                        <LabelValue label="Status" value={<Badge bg={statusVariant(data.status)}>{data.status}</Badge>} />
                        <LabelValue label="Preferred Channel" value={data.preferredCommChannel} />
                        <LabelValue label="Verified" value={data.isVerified ? 'Yes' : 'No'} />
                        <LabelValue label="Verified At" value={data.verifiedAt ? formatDate(data.verifiedAt) : '—'} />
                      </SectionCard>
                    </div>
                    <div className="col-lg-6">
                      <SectionCard title="Company" icon="fa-building">
                        <LabelValue label="Registered Name" value={corp?.registeredName} />
                        <LabelValue label="Trading Name" value={corp?.tradingName} />
                        <LabelValue label="Entity Type" value={corp?.legalEntityType ? (ENTITY_TYPE_LABELS[corp.legalEntityType] ?? corp.legalEntityType) : undefined} />
                        <LabelValue
                          label={`Registration Number${corp?.registrationNumberType ? ` (${REGISTRATION_NUMBER_TYPE_LABELS[corp.registrationNumberType] ?? corp.registrationNumberType})` : ''}`}
                          value={corp?.registrationNumber}
                        />
                        <LabelValue label="Industry Sector" value={corp?.industrySector} />
                        <LabelValue label="Tax Number" value={(corp as any)?.taxNumber} />
                      </SectionCard>
                    </div>
                  </div>
                  {/* #38: personal default-charge terms for all this customer's loans */}
                  {id && <div className="mt-3"><CustomerChargeTermsCard customerPartyId={id} /></div>}
                </Tab.Pane>

                <Tab.Pane eventKey="company">
                  <SectionCard title="Company Information" icon="fa-building">
                    <div className="row">
                      <div className="col-md-6">
                        <LabelValue label="Registered Name" value={corp?.registeredName} />
                        <LabelValue label="Trading Name" value={corp?.tradingName} />
                        <LabelValue label="Registration Number" value={corp?.registrationNumber} />
                        <LabelValue label="Industry Sector" value={corp?.industrySector} />
                        <LabelValue label="Business Activity" value={corp?.businessActivity} />
                      </div>
                      <div className="col-md-6">
                        <LabelValue label="Tax Number" value={(corp as any)?.taxNumber} />
                        <LabelValue label="VAT Number" value={(corp as any)?.vatNumber} />
                        <LabelValue label="Annual Turnover" value={(corp as any)?.annualTurnover?.toLocaleString()} />
                        <LabelValue label="Employees" value={(corp as any)?.employeeCount} />
                        <LabelValue label="Established" value={corp?.establishmentDate ? formatDate(corp.establishmentDate) : '—'} />
                      </div>
                    </div>
                  </SectionCard>
                </Tab.Pane>

                <Tab.Pane eventKey="addresses">
                  <SectionCard title="Addresses" icon="fa-map-marker">
                    {data.addresses && data.addresses.length > 0 ? (
                      <div className="row g-3">
                        {data.addresses.map((addr: Address, idx: number) => (
                          <div key={idx} className="col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                              <Card.Body>
                                <div className="d-flex align-items-center mb-2">
                                  <span className="badge bg-light text-dark border me-2">{addr.addressType}</span>
                                  {addr.isPrimary && <span className="badge bg-primary">Primary</span>}
                                </div>
                                <div className="text-dark fw-semibold">{addr.streetAddress}</div>
                                <div className="text-muted small">{[addr.suburb, addr.city, addr.stateProvince, addr.postalCode].filter(Boolean).join(', ')}</div>
                                <div className="text-muted small mt-1">{addr.country}</div>
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">No addresses captured.</div>
                    )}
                  </SectionCard>
                </Tab.Pane>

                <Tab.Pane eventKey="contacts">
                  {/* The person the business deals through, named first: the rows below are
                      only phone numbers and addresses, which say nothing about who to ask for. */}
                  {(corp?.representativeFirstName || corp?.representativeCapacity) && (
                    <SectionCard title="Company Representative" icon="fa-user-tie">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <LabelValue
                            label="Name"
                            value={[corp?.representativeFirstName, corp?.representativeLastName]
                              .filter(Boolean).join(' ') || undefined}
                          />
                          <LabelValue
                            label="Capacity"
                            value={corp?.representativeCapacity
                              ? (REPRESENTATIVE_CAPACITY_LABELS[corp.representativeCapacity] ?? corp.representativeCapacity)
                              : undefined}
                          />
                        </div>
                        <div className="col-md-6">
                          <LabelValue label="ID Number" value={corp?.representativeIdNumber} />
                          <LabelValue label="Mandate" value={corp?.representativeMandate} />
                        </div>
                      </div>
                    </SectionCard>
                  )}
                  <SectionCard title="Contacts" icon="fa-users">
                    {data.contacts && data.contacts.length > 0 ? (
                      <div className="row g-3">
                        {data.contacts.map((c: ContactDetail, idx: number) => (
                          <div key={idx} className="col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                              <Card.Body>
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fa fa-user-circle text-primary me-2" />
                                  <span className="badge bg-light text-dark border">{c.contactType}</span>
                                  {c.primary && <span className="badge bg-primary ms-2">Primary</span>}
                                </div>
                                <div className="fw-semibold">{c.contactValue}</div>
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">No contacts captured.</div>
                    )}
                  </SectionCard>
                </Tab.Pane>

                <Tab.Pane eventKey="bank">
                  <SectionCard title="Bank Details" icon="fa-university">
                    {data.bankDetails && data.bankDetails.length > 0 ? (
                      <div className="row g-3">
                        {data.bankDetails.map((b: BankDetail, idx: number) => (
                          <div key={idx} className="col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                              <Card.Body>
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fa fa-bank text-primary me-2" />
                                  <span className="badge bg-light text-dark border">{b.accountType}</span>
                                  {b.isPrimary && <span className="badge bg-primary ms-2">Primary</span>}
                                </div>
                                <LabelValue label="Account Holder" value={b.accountHolderName} />
                                <LabelValue label="Bank" value={b.bankName} />
                                <LabelValue label="Account Number" value={b.accountNumber} />
                                <LabelValue label="Branch Code" value={b.branchCode} />
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">No bank details captured.</div>
                    )}
                  </SectionCard>
                </Tab.Pane>

                <Tab.Pane eventKey="directors">
                  <SectionCard title="Directors" icon="fa-id-badge">
                    {corp?.directors && corp.directors.length > 0 ? (
                      <div className="row g-3">
                        {corp.directors.map((d, idx) => (
                          <div key={idx} className="col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                              <Card.Body>
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fa fa-user-tie text-primary me-2" />
                                  <span className="fw-bold">{d.firstName} {d.lastName}</span>
                                </div>
                                <LabelValue label="ID Number" value={d.idNumber} />
                                <LabelValue label="Nationality" value={d.nationality} />
                                <LabelValue label="Designation" value={d.designation} />
                                <LabelValue label="Ownership %" value={`${d.ownershipPercentage ?? 0}%`} />
                                <LabelValue label="Executive" value={d.isExecutive ? 'Yes' : 'No'} />
                                <LabelValue
                                  label="Nominated for communication"
                                  value={(d as any).isCommunicationContact ? 'Yes' : 'No'}
                                />
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">No directors captured.</div>
                    )}
                  </SectionCard>
                </Tab.Pane>

                <Tab.Pane eventKey="comments">
                  {data.id ? (
                    <>
                      <CustomerComments customerId={data.id} className="mt-2" />
                      <CustomerCollectionNotes customerId={data.id} className="mt-4" />
                    </>
                  ) : (
                    <div className="alert alert-info">
                      <i className="fa fa-info-circle me-2"></i>
                      Customer ID is not available. Comments cannot be loaded.
                    </div>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
